import { Link } from "@shared/client/navigation";
import { useMemo } from "react";
import { useT } from "@shared/client/locale";
import { Bar, BarChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@shared/components/ui/chart";
import { PIPELINE_STAGES, type PipelineCounts } from "@shared/lib/dashboard-metrics";

/**
 * Répartition du pipeline en part-à-tout plutôt qu'en quatre nombres alignés :
 * l'œil compare des longueurs bien mieux que des chiffres, et l'équilibre entre
 * étapes est justement la question posée.
 *
 * Échelle ordinale (une progression, pas des identités) : une seule teinte du
 * clair au foncé, rampe validée `--ordinal`. Séparation par un liseré de 2 px
 * dans la couleur de surface, jamais par un contour coloré.
 */
const COLORS = {
  fresh: "var(--ordinal-1)",
  toContact: "var(--ordinal-2)",
  engaged: "var(--ordinal-4)",
  won: "var(--ordinal-5)",
  other: "var(--ordinal-rest)",
};

export function PipelineBar({ counts, total }: { counts: PipelineCounts; total: number }) {
  const copy = useT().dashboard.pipeline;
  const chartConfig = useMemo(
    () =>
      ({
        fresh: { label: copy.stages.fresh, color: COLORS.fresh },
        toContact: { label: copy.stages.toContact, color: COLORS.toContact },
        engaged: { label: copy.stages.engaged, color: COLORS.engaged },
        won: { label: copy.stages.won, color: COLORS.won },
        other: { label: copy.stages.other, color: COLORS.other },
      }) satisfies ChartConfig,
    [copy],
  );
  const stages = PIPELINE_STAGES.map((stage) => ({ ...stage, label: copy.stages[stage.key], count: counts[stage.key] }));
  const other = total - stages.reduce((sum, stage) => sum + stage.count, 0);

  if (total === 0) {
    return (
      <p className="m-0 text-[13px] leading-relaxed text-muted-foreground">
        {copy.empty}
      </p>
    );
  }

  const row = {
    label: copy.title,
    ...Object.fromEntries(stages.map((stage) => [stage.key, stage.count])),
    other: Math.max(other, 0),
  };

  const segments = [...PIPELINE_STAGES.map((stage) => stage.key), "other" as const];

  return (
    <div className="grid gap-4">
      <ChartContainer
        className="aspect-auto h-2.5 w-full"
        config={chartConfig}
        role="img"
        aria-label={copy.breakdown(
          total,
          stages.filter((stage) => stage.count > 0).map((stage) => copy.stageCount(stage.count, stage.label)),
        )}
      >
        <BarChart accessibilityLayer data={[row]} layout="vertical" margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <XAxis type="number" hide />
          <YAxis dataKey="label" type="category" hide />
          <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel indicator="dot" />} />
          {segments.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="pipeline"
              fill={`var(--color-${key})`}
              stroke="var(--background)"
              strokeWidth={2}
              radius={index === 0 ? [3, 0, 0, 3] : index === segments.length - 1 ? [0, 3, 3, 0] : 0}
            />
          ))}
        </BarChart>
      </ChartContainer>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
        {stages.map((stage) => (
          <div className="flex items-center justify-between gap-2 text-[12.5px]" key={stage.key}>
            <dt className="flex items-center gap-2 text-muted-foreground">
              <i
                aria-hidden="true"
                className="size-2 shrink-0 rounded-[2px]"
                style={{ background: COLORS[stage.key] }}
              />
              {stage.label}
            </dt>
            <dd className="m-0 font-medium tabular-nums">{stage.count}</dd>
          </div>
        ))}
      </dl>

      <p className="m-0 flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
        <span>{copy.tracked(total)}</span>
        {counts.fresh > 0 && (
          <Link className="text-primary hover:underline" href="/app/leads?filter=new">
            {copy.openUnread(counts.fresh)}
          </Link>
        )}
      </p>
    </div>
  );
}
